import { AutoField, FieldLabel } from "@measured/puck";
import { Box, useTheme } from "@mui/material";
import { t } from "i18next";
import ToggleOn from "@mui/icons-material/ToggleOn";
import Image from "@mui/icons-material/Image";
import Straighten from "@mui/icons-material/Straighten";
import TextFields from "@mui/icons-material/TextFields";
import InsertDriveFile from "@mui/icons-material/InsertDriveFile";
import AlignHorizontalLeft from "@mui/icons-material/AlignHorizontalLeft";

import type { PdfHeaderConfig } from "src/components/Puck/config/root";
import NativePuckTitleField from "src/components/Puck/config/fields/NativePuckTitleField";
import { TokenizedTextField } from "src/components/Puck/config/fields/TokenizedTextField";

type PdfHeaderFieldProps = {
    value?: PdfHeaderConfig;
    onChange: (value: PdfHeaderConfig) => void;
    readOnly?: boolean;
};

export const PdfHeaderField = ({ value, onChange, readOnly }: PdfHeaderFieldProps) => {
    const theme = useTheme();
    const header = value ?? {};

    const toNumber = (v: unknown): number | undefined => {
        if (typeof v === "number" && Number.isFinite(v)) return v;
        if (typeof v === "string") {
            const parsed = Number(v);
            return Number.isFinite(parsed) ? parsed : undefined;
        }
        return undefined;
    };

    const updateField = <K extends keyof PdfHeaderConfig>(key: K, fieldValue: PdfHeaderConfig[K]) => {
        onChange({ ...header, [key]: fieldValue });
    };

    return (
        <Box>
            <NativePuckTitleField
                label={t("puck.pdf_header.title", "Header")}
                subtitle={t("puck.pdf_header.subtitle", "Configure PDF header with logo, title and subtitle")}
                icon="edit"
            />
            <Box sx={{ pt: theme.spacing(3) }}>
                <FieldLabel label={t("puck.pdf_header.enabled", "Enable header")} icon={<ToggleOn fontSize="small" />}>
                    <AutoField
                        field={{
                            type: "radio",
                            options: [
                                { label: t("common.yes", "Yes"), value: true },
                                { label: t("common.no", "No"), value: false },
                            ],
                        }}
                        onChange={(v) => updateField("enabled", v as boolean)}
                        value={header.enabled ?? false}
                        readOnly={readOnly}
                    />
                </FieldLabel>
            </Box>

            {header.enabled && (
                <>
                    <Box sx={{ pt: theme.spacing(3) }}>
                        <FieldLabel label={t("puck.pdf_header.logo_url", "Logo URL")} icon={<Image fontSize="small" />}>
                            <AutoField
                                field={{ type: "text" }}
                                onChange={(v) => updateField("logoUrl", v as string)}
                                value={header.logoUrl ?? ""}
                                readOnly={readOnly}
                            />
                        </FieldLabel>
                    </Box>

                    <Box sx={{ pt: theme.spacing(3) }}>
                        <FieldLabel
                            label={t("puck.pdf_header.logo_height", "Logo height (px)")}
                            icon={<Straighten fontSize="small" />}
                        >
                            <AutoField
                                field={{ type: "number", min: 0, max: 100 }}
                                onChange={(v) => updateField("logoHeight", toNumber(v))}
                                value={header.logoHeight ?? 30}
                                readOnly={readOnly}
                            />
                        </FieldLabel>
                    </Box>

                    <Box sx={{ pt: theme.spacing(3) }}>
                        <FieldLabel
                            label={t("puck.pdf_header.header_title", "Title")}
                            icon={<TextFields fontSize="small" />}
                        >
                            <TokenizedTextField
                                onChange={(v) => updateField("title", v)}
                                value={header.title ?? ""}
                                placeholder={t("puck.pdf_header.title_placeholder", "Enter title (use { for tokens)")}
                                readOnly={readOnly}
                            />
                        </FieldLabel>
                    </Box>

                    <Box sx={{ pt: theme.spacing(3) }}>
                        <FieldLabel
                            label={t("puck.pdf_header.subtitle", "Subtitle")}
                            icon={<InsertDriveFile fontSize="small" />}
                        >
                            <TokenizedTextField
                                onChange={(v) => updateField("subtitle", v)}
                                value={header.subtitle ?? ""}
                                placeholder={t(
                                    "puck.pdf_header.subtitle_placeholder",
                                    "Enter subtitle (use { for tokens)",
                                )}
                                readOnly={readOnly}
                            />
                        </FieldLabel>
                    </Box>

                    <Box sx={{ pt: theme.spacing(3) }}>
                        <FieldLabel
                            label={t("puck.pdf_header.alignment", "Alignment")}
                            icon={<AlignHorizontalLeft fontSize="small" />}
                        >
                            <AutoField
                                field={{
                                    type: "radio",
                                    options: [
                                        { label: t("common.left", "Left"), value: "left" },
                                        { label: t("common.center", "Center"), value: "center" },
                                        { label: t("common.right", "Right"), value: "right" },
                                    ],
                                }}
                                onChange={(v) => updateField("alignment", v as PdfHeaderConfig["alignment"])}
                                value={header.alignment ?? "left"}
                                readOnly={readOnly}
                            />
                        </FieldLabel>
                    </Box>
                </>
            )}
        </Box>
    );
};

export default PdfHeaderField;
